/* eslint-disable camelcase */
import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import { OFFICER_STATUSES } from "@constants";
import { Modal, Image } from "../../../../../components";

import "./MultiPositionModal.scss";

const MultiPositionModal = props => {
	const [officerContact, setOfficerContact] = useState();
	const { lang } = global;
	const { show, timezone, onCancel, jobEvent, jobPermissions, onEditAssignment } = props;

	/**
	 * Renders a label to indicate an officer's status while working a job
	 * @param {Number} statusId A number representing the officer status
	 */
	const getStatusDescription = statusId => {
		const status = Object.values(OFFICER_STATUSES).find(
			officerStatus => officerStatus.type === statusId,
		);
		return <div className={`label label-${status.color}`}>{status.description}</div>;
	};

	/**
	 * @param {Object} officer A data object representing an officer
	 * @returns {String} Formatted officer name and contact information to show within the modal
	 */
	const getContactInfo = officer => {
		const { OfficerName, CellPhone, Email } = officer;
		return `${OfficerName}\n${lang.t("Phone")}: ${utils.formatPhoneNumber(CellPhone)}\n${lang.t("Email")}: ${Email}`;
	};

	/**
	 * Renders all officers assigned to a position that is part of a given job event (`props.jobEvent`)
	 * @param {Number} positionId The ID number for the position
	 */
	const renderAssignedOfficers = positionId => {
		const { Jobs } = jobEvent;
		const job = Jobs.find(({ Id }) => Id === positionId) || {};

		const assignedOfficers = (job.AssignedOfficers || []).map(item => ({
			...item.OfficerProfile,
			OfficerName: `${item.OfficerProfile.FirstName} ${item.OfficerProfile.LastName}`,
			ClockIn: item.ClockIn,
			ClockOut: item.ClockOut,
			OfficerStatus: item.OfficerStatus,
			UserId: item.UserId,
		}));

		const externalOfficers = (job.ExternalOfficers || []).map(item => ({
			...item,
			isExternal: true,
		}));

		const officers = [...assignedOfficers, ...externalOfficers];

		return (
			<Fragment>
				<If condition={officers.length === 0}>
					<div className="col-xs-12 text-muted empty-text">
						{lang.t("No officers assigned")}
					</div>
				</If>
				<For
					each="officer"
					of={officers}
				>
					<div
						className="col-xs-12"
						key={`multi-pos-officer-${officer.isExternal ? `e-${officer.Id}` : officer.UserId}`}
					>
						<div
							className={`v-align-sm row multi-officer${officer.isExternal ? " external-officer" : ""}`}
							role="button"
							tabIndex="0"
							onClick={() =>
								setOfficerContact(
									officerContact === officer.UserId ? undefined : officer.UserId,
								)
							}
							onKeyPress={({ key }) => {
								if (key === "Enter")
									setOfficerContact(
										officerContact === officer.UserId
											? undefined
											: officer.UserId,
									);
							}}
						>
							<div className="col-xs-8">
								<Image
									className="officer-image img-circle"
									src={officer.ContactPhotoUrl || ""}
									fallbackSrc="avatar"
									title={officer.isExternal ? undefined : getContactInfo(officer)}
								/>
								<span title={officer.OfficerName}>{`${officer.OfficerName} `}</span>
								<If condition={!officer.isExternal && officer.OfficerStatus !== 0}>
									{getStatusDescription(officer.OfficerStatus)}
								</If>
							</div>
							<div className="col-xs-4 text-right">
								<If condition={officer.isExternal}>
									<em>{lang.t("Non-RollKall Officer")}</em>
								</If>
								<If condition={officer.OfficerStatus !== 0 && !officer.isExternal}>
									<div className="clockinfo">
										{lang.t("Clock In")}
										<br />
										{officer.ClockIn
											? utils.formatTime(
													new Date(`${officer.ClockIn}Z`),
													true,
													timezone,
												)
											: "--"}
									</div>
									<div className="clockinfo">
										{lang.t("Clock Out")}
										<br />
										{officer.ClockOut
											? utils.formatTime(
													new Date(`${officer.ClockOut}Z`),
													true,
													timezone,
												)
											: "--"}
									</div>
								</If>
							</div>
							<If
								condition={!officer.isExternal && officerContact === officer.UserId}
							>
								<div className="col-xs-12 officer-contact">
									<strong>{lang.t("Contact Info")}</strong>
									<br />
									{lang.t("Phone")}:{" "}
									<a href={`tel:${officer.CellPhone}`}>
										{utils.formatPhoneNumber(officer.CellPhone)}
									</a>
									<br />
									{lang.t("Email")}:{" "}
									<a href={`mailto:${officer.Email}`}>{officer.Email}</a>
								</div>
							</If>
						</div>
					</div>
				</For>
			</Fragment>
		);
	};

	/**
	 * Sorts the list of jobs by:
	 *  - Start date ascending
	 *  - Secondarily, by Job ID (position ID) - in the event of same start dates
	 *  - Thirdly, sort by reporting and supervisory positions
	 * @param {Array} jobs The array of jobs
	 * @returns {Array} The sorted array of jobs
	 */
	const sortJobs = jobs => {
		if (!jobs) return [];

		return jobs.sort((a, b) => {
			if (new Date(a.StartDateTime) < new Date(b.StartDateTime)) return -1;
			if (new Date(a.StartDateTime) > new Date(b.StartDateTime)) return 1;
			if (a.Id < b.Id) return -1;
			if (a.Id > b.Id) return 1;
			if (a.PositionGroupId === b.PositionGroupId) {
				if (a.SupervisorPosition && !b.SupervisorPosition) return -1;
				if (!a.SupervisorPosition && b.SupervisorPosition) return 1;
			}
			return 0;
		});
	};

	return (
		<Modal
			show={show}
			id="multipos-modal"
			onHide={() => onCancel()}
		>
			<Modal.Header>
				<Modal.Title>Positions for {jobEvent.Name}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<For
					each="position"
					of={sortJobs(jobEvent.Jobs)}
				>
					<Fragment key={`modal-position_${position.Id}`}>
						<div className="position row v-align-sm">
							<div className="col-sm-9">
								<div className="row">
									<div className="col-xs-6">
										<strong>
											{position.Title}
											{position.SupervisorPosition ? " *" : ""}
										</strong>
										<br />
										<If condition={jobEvent.JobEventTypeId === 6}>
											<small>
												{utils.formatDate(
													`${position.StartDateTime}Z`,
													"en-US",
													timezone,
													"numeric",
												)}
												<br />
												{utils.formatTime(
													new Date(`${position.StartDateTime}Z`),
													true,
													timezone,
												)}
												&nbsp;-&nbsp;
												{utils.formatTime(
													new Date(`${position.EndDateTime}Z`),
													true,
													timezone,
												)}
											</small>
											<If condition={position.FlexJob}>
												&nbsp;
												<span
													className="label label-info"
													title={`${position.FlexJobMinHours} ${lang.t("Hr Minimum")}`}
												>
													{lang.t("FLEX")}
												</span>
											</If>
										</If>
									</div>
									<div className="col-xs-3 text-center">
										<strong>{`${utils.currencyFormatter(position.Rate, false)}${lang.t("_hour")}`}</strong>
									</div>
									<div className="col-xs-3 text-center text-lowercase">
										<strong>{`${position.AssignedOfficersCount}/${position.OfficersNeeded} ${lang.t("Filled")}`}</strong>
									</div>
								</div>
							</div>
							<div className="col-sm-3 hidden-xs">
								<button
									type="button"
									className="btn btn-primary btn-block"
									onClick={() => onEditAssignment(jobEvent, position.Id)}
									disabled={
										!jobPermissions.allowAssign && !jobPermissions.allowUnassign
									}
									title={
										!jobPermissions.allowAssign && !jobPermissions.allowUnassign
											? lang.t("dynamic.Assignment managed by", {
													agency: jobEvent.AgencyName,
												})
											: undefined
									}
								>
									{lang.t("Assign")}
								</button>
							</div>
							<If condition={position.ApplicationsPendingCount > 0}>
								<div className="col-xs-12">
									{/* eslint-disable-next-line camelcase */}
									{lang.t("dynamic.New Applicant", {
										smart_count: position.ApplicationsPendingCount,
									})}
								</div>
							</If>
							<If condition={position.Details}>
								<div className="col-xs-12">{position.Details}</div>
							</If>
							<If condition={position.OfficerRequirements}>
								<div className="col-xs-12">
									{lang.t("Requirements")}:&nbsp;
									<em>{position.OfficerRequirements}</em>
								</div>
							</If>
						</div>
						<div className="row">
							{renderAssignedOfficers(position.Id)}
							<div className="col-xs-6 col-xs-offset-3 visible-xs">
								<If
									condition={
										jobPermissions.allowAssign || jobPermissions.allowUnassign
									}
								>
									<button
										type="button"
										className="btn btn-primary btn-block"
										onClick={() => onEditAssignment(jobEvent, position.Id)}
									>
										{lang.t("Assign")}
									</button>
									<br />
								</If>
							</div>
						</div>
					</Fragment>
				</For>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					className="btn btn-link"
					onClick={() => onCancel()}
				>
					{lang.t("Close")}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

MultiPositionModal.propTypes = {
	show: PropTypes.bool,
	timezone: PropTypes.string,
	onCancel: PropTypes.func,
	jobEvent: PropTypes.object,
	jobPermissions: PropTypes.object,
	onEditAssignment: PropTypes.func,
};

MultiPositionModal.defaultProps = {
	show: false,
	timezone: undefined,
	onCancel: PropTypes.func,
	jobEvent: { Jobs: [] },
	jobPermissions: {},
	onEditAssignment: () => {},
};

export default MultiPositionModal;
